import { useState } from 'react';

import { useLocalSetting } from '@/lib/hooks';
import { useSelectedAccount } from '@/lib/accounts/context';
import { useMetadataContext } from '@/explore/metadata-context';
import { ChatMode, ChatSource } from '@/explore/types';

import { ensureValidSources, getAvailableSources } from './utils';

export interface ChatSettings {
  mode: ChatMode;
  sources: string[];
  [key: string]: ChatMode | string[];
}

interface Params {
  mode?: ChatMode;
  sources?: string[];
}

export const useChatSettings = ({ sources, mode }: Params = {}) => {
  const account = useSelectedAccount();
  const { models } = useMetadataContext();
  const availableSources = getAvailableSources(account.integrations, models);

  const [chatSettings, setChatSettings] = useLocalSetting<ChatSettings>(
    `conversation.chat.settings.${account.accountId}`,
    {
      mode: 'normal',
      sources: Array.isArray(account.features?.defaultSources)
        ? account.features?.defaultSources
        : [availableSources.length > 0 ? availableSources[0] : ChatSource.Database],
    },
  );

  const defaultSources = sources ?? chatSettings.sources ?? [availableSources[0]];

  const [selectedSources, setSelectedSources] = useState<string[]>(defaultSources);
  const [selectedMode, setSelectedMode] = useState<ChatMode>(mode ?? chatSettings.mode ?? 'normal');

  const setSettings = (settings: ChatSettings) => {
    if (settings.sources.length === 0 && availableSources.length > 0) {
      settings.sources = [availableSources[0]];
    }

    setSelectedSources(settings.sources);
    setSelectedMode(settings.mode);
    setChatSettings(settings);
  };

  return {
    selectedSources: ensureValidSources(selectedSources, availableSources),
    selectedMode,
    setSettings,
  };
};
